import React, { useEffect, useState, FC, Fragment } from 'react';
import { colorPalette, docGeniusPalette } from 'styles/partials/colors';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ViewSideBar from '@mui/icons-material/ViewSidebar';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import {
  AppBar,
  Box,
  Divider,
  IconButton,
  InputBase,
  SelectChangeEvent,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import Button from '../Button/Button';
import styles from './DocumentNavbar.module.css';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  Template,
  selectDocumentName,
  selectDocumentType,
  selectInitialDocument,
  selectNoteContent,
  selectNoteTitle,
  selectTemplateDocument,
  toggleSidebar,
  updateDocumentName,
  updateSavingMethod,
  updateSelectedRecipient,
} from 'features/document/documentSlice';
import {
  usePatchDocumentMutation,
  usePostDocumentMutation,
  usePutLinkDocumentsMutation,
  useSendDocumentMutation,
} from 'features/document/documentApi';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSelfUserQuery } from 'features/user/userApi';
import RecipientSelect from 'components/RecipientSelect/RecipientSelect';
import { Recipient, RecipientRole, documentStatus, ElementType } from 'features/document/constants';
import { EditRecipientModal } from './components/EditRecipientModal';
import { findElementsWithImage, getPagesFilesId } from 'utils/findElementsWithImage';
import SendFileModal from './components/SendFileModal/SendFileModal';
import { formatToTitleCase } from 'utils/formatDocumentType';
import { ViewSidebarOutlined } from '@mui/icons-material';
import { SplitButton } from 'components/SplitButton/SplitButton';
import {
  usePostTemplateMutation,
  usePutLinkTemplatesMutation,
  usePutTemplateMutation,
} from 'features/template/templateApi';
import { useGetAllSharedCredentialsQuery } from 'features/docuSign/docuSignApi';
import { toast } from 'react-toastify';
interface Props {
  isCreate: boolean;
  onClickVersionHistoryButton: () => void;
}

const DocumentNavBar: FC<Props> = ({ isCreate, onClickVersionHistoryButton }) => {
  const documentName = useAppSelector(selectDocumentName);
  const noteTitle = useAppSelector(selectNoteTitle);
  const noteContent = useAppSelector(selectNoteContent);
  const documentType = useAppSelector(selectDocumentType);
  const formattedDocumentType = formatToTitleCase(documentType);
  const initialDocument = useAppSelector(selectInitialDocument);
  const isSidebarOpen = useAppSelector(state => state.document.isSidebarOpen);

  const { id: documentId } = useParams();
  const { data } = useGetSelfUserQuery();
  const { data: credentials, isLoading } = useGetAllSharedCredentialsQuery('');
  const templateJson = useAppSelector(selectTemplateDocument);
  const recipients = templateJson.recipients;
  const [postDocument] = usePostDocumentMutation();
  const [postTemplate] = usePostTemplateMutation();
  const [patchDocument] = usePatchDocumentMutation();
  const [putTemplate] = usePutTemplateMutation();
  const [putLinkFiles] = usePutLinkDocumentsMutation();
  const [putLinkFilesTemplate] = usePutLinkTemplatesMutation();
  const [sendDocument, { isSuccess }] = useSendDocumentMutation();
  const [savedLabel, setSavedLabel] = useState<boolean>(false);

  const { pathname } = location;
  const isTemplateView = pathname.includes('templates');

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const getImagesId = (templateJson: Template) => {
    const imageElements = findElementsWithImage(templateJson);
    const pagesFilesId = getPagesFilesId(templateJson);
    const elementsFilesId = imageElements
      .map(el => {
        if (el.type === ElementType.Header || el.type === ElementType.Footer)
          return [el.content.backgroundImage.id, el.content.logo.id];
        return el.content.id;
      })
      .flat(1)
      .filter(el => el !== null)
      .map(el => ({ id: el }));

    return [...pagesFilesId, ...elementsFilesId];
  };
  const ids = getImagesId(templateJson);
  const [name, setName] = useState<string>(documentName);

  const [recipient, setRecipient] = useState<Recipient | null>(recipients[0] || null);
  const [docuSignEmail, setDocuSignEmail] = useState<string | null>(null);
  const [openRecipientModal, setOpenRecipientModal] = useState(false);
  const [openSendFileModal, setOpenSendFileModal] = useState(false);

  useEffect(() => {
    if (credentials && credentials.data) {
      const { data: sharedCredentials } = credentials;
      const [firstCredential] = sharedCredentials;
      if (firstCredential) {
        setDocuSignEmail(firstCredential.docuSignEmail);
      }
    }
  }, [credentials]);

  useEffect(() => {
    if (documentName) {
      setName(documentName);
    }
  }, [documentName]);

  useEffect(() => {
    if (recipients) {
      if (!recipient) {
        const firstSigner = recipients.find(recipient => recipient.role === RecipientRole.SIGNER);
        if (firstSigner) setRecipient(firstSigner);
      }
    }
  }, [recipients]);

  useEffect(() => {
    dispatch(updateSelectedRecipient(recipient));
  }, [recipient]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`The file "${documentName}" was sent successfully`, {
        position: toast.POSITION.BOTTOM_CENTER,
        style: {
          background: colorPalette.neutralGray3,
          color: '#F2F3F3',
          minWidth: '570px',
        },
      });
    }
  }, [isSuccess]);

  useEffect(() => {
    dispatch(updateSavingMethod(isTemplateView ? 'TEMPLATE' : 'FILE'));
  }, [initialDocument]);

  const assignOrderIdToRecipients = (templateJson: Template) => {
    const { recipients } = templateJson;
    const updatedRecipients = recipients.map((recipient, index) => {
      return {
        ...recipient,
        orderId: index + 1,
      };
    });

    return { ...templateJson, recipients: updatedRecipients };
  };

  const handleOnChangeRecipient = (event: SelectChangeEvent) => {
    const selectedValue = event.target.value;
    const selectedRecipient = recipients.find(recipient => recipient.fullName === selectedValue);
    if (selectedValue === 'edit_recipients') {
      handleOpenRecipientModal();
    } else if (selectedRecipient) {
      setRecipient(selectedRecipient);
      dispatch(updateSelectedRecipient(selectedRecipient));
    }
  };

  const handleOpenRecipientModal = () => {
    setOpenRecipientModal(true);
  };

  const handleCloseRecipientModal = () => {
    setOpenRecipientModal(false);
  };

  const handleCloseSendFileModal = () => {
    setOpenSendFileModal(false);
  };
  const handleSavedLabel = () => {
    setSavedLabel(true);
    setTimeout(() => {
      setSavedLabel(false);
    }, 1500);
  };
  const handleSaveName = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>) => {
    const name = e.target.value.trim();
    if (name !== documentName) {
      dispatch(updateDocumentName(name));
      handleSavedLabel();
    }
  };

  const handleGoBack = () => {
    if (!initialDocument.id) {
      handleCreate(isTemplateView, true);
    }
    navigate(isTemplateView ? '/templates' : '/documents');
  };

  const handleSave = async (isTemplate: boolean) => {
    const defaultName = isTemplate ? `Untitled ${formattedDocumentType} Template` : `Untitled ${formattedDocumentType}`;
    if (initialDocument && initialDocument.id) {
      if (isTemplateView && isTemplate) {
        const savedTemplate = await putTemplate({
          ...initialDocument,
          name: documentName || defaultName,
          status: !documentName || documentName === defaultName ? documentStatus.Draft : documentStatus.Active,
          noteContent,
          noteTitle,
          templateJson: JSON.stringify(assignOrderIdToRecipients(templateJson)),
          id: initialDocument.id,
        }).unwrap();
        handleSavedLabel();
        if (savedTemplate && ids.length > 0) {
          putLinkFilesTemplate({ id: savedTemplate.data.id, files: ids });
          handleSavedLabel();
        }
      } else if (isTemplateView && !isTemplate) {
        const createdDocument = await postDocument({
          name: documentName || defaultName,
          sent: false,
          status: !documentName || documentName === defaultName ? documentStatus.Draft : documentStatus.Active,
          isFavorite: false,
          userId: data?.data.id,
          templateJson: JSON.stringify(assignOrderIdToRecipients(templateJson)),
          noteTitle: noteTitle,
          noteContent: noteContent,
          type: documentType,
        }).unwrap();

        if (createdDocument && ids.length > 0) {
          putLinkFiles({ id: createdDocument.data.id, files: ids });
        }
        navigate(`/documents/edit/${documentType.toLowerCase()}/${createdDocument.data.id}`);
      } else if (!isTemplateView && isTemplate) {
        const createdTemplate = await postTemplate({
          name: documentName || defaultName,
          isFavorite: false,
          status: !documentName || documentName === defaultName ? documentStatus.Draft : documentStatus.Active,
          templateJson: JSON.stringify(assignOrderIdToRecipients(templateJson)),
          type: documentType,
        }).unwrap();
        if (createdTemplate && ids.length > 0) {
          putLinkFilesTemplate({ id: createdTemplate.data.id, files: ids });
        }
        navigate(`/templates/edit/${documentType.toLowerCase()}/${createdTemplate.data.id}`);
      } else {
        const savedDocument = await patchDocument({
          ...initialDocument,
          name: documentName || defaultName,
          status: !documentName || documentName === defaultName ? documentStatus.Draft : documentStatus.Active,
          noteContent,
          noteTitle,
          templateJson: JSON.stringify(assignOrderIdToRecipients(templateJson)),
          id: initialDocument.id,
        }).unwrap();
        handleSavedLabel();

        if (savedDocument && ids.length > 0) {
          putLinkFiles({ id: savedDocument.data.id, files: ids });
          handleSavedLabel();
        }
      }
    }
  };

  const handleCreate = async (isTemplate: boolean, goBack?: boolean) => {
    const defaultName = isTemplate ? `Untitled ${formattedDocumentType} Template` : `Untitled ${formattedDocumentType}`;
    if (isTemplate) {
      const createdTemplate = await postTemplate({
        name: documentName || defaultName,
        isFavorite: false,
        templateJson: JSON.stringify(assignOrderIdToRecipients(templateJson)),
        status: !documentName || documentName === defaultName || goBack ? documentStatus.Draft : documentStatus.Active,
        type: documentType,
      }).unwrap();
      if (createdTemplate && ids.length > 0) {
        putLinkFilesTemplate({ id: createdTemplate.data.id, files: ids });
      }
      if (!goBack) {
        navigate(`/templates/edit/${documentType.toLowerCase()}/${createdTemplate.data.id}`);
      }
    } else {
      const createdDocument = await postDocument({
        name: documentName || defaultName,
        sent: false,
        status: !documentName || documentName === defaultName || goBack ? documentStatus.Draft : documentStatus.Active,
        isFavorite: false,
        userId: data?.data.id,
        templateJson: JSON.stringify(assignOrderIdToRecipients(templateJson)),
        noteTitle: noteTitle,
        noteContent: noteContent,
        type: documentType,
      }).unwrap();

      if (createdDocument && ids.length > 0) {
        putLinkFiles({ id: createdDocument.data.id, files: ids });
      }
      if (!goBack) {
        navigate(`/documents/edit/${documentType.toLowerCase()}/${createdDocument.data.id}`);
      }
    }
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleSend = async () => {
    if (documentId) {
      await sendDocument({ documentId });
      navigate('/documents');
    }
  };

  const handleChangeSaveOption = (option: string) => {
    const savingMethod = option.toUpperCase().includes('TEMPLATE') ? 'TEMPLATE' : 'FILE';
    dispatch(updateSavingMethod(savingMethod));
  };

  const renderButtons = () => {
    if (isCreate) {
      const options = ['Create File', 'Create As A Template'];
      return (
        <Box display='flex' alignItems='center' gap='1rem' height='40px'>
          <SplitButton
            options={options}
            onClick={option => handleCreate(option === options[1])}
            onChange={handleChangeSaveOption}
            initialIndex={isTemplateView ? 1 : 0}
          />
        </Box>
      );
    }

    const saveOptions = ['Save File', 'Save as A Template'];

    return (
      <Box display='flex' alignItems='center' gap='1rem' height='40px'>
        {savedLabel && <Typography className={styles.navbarText1}> Saved! </Typography>}
        <Tooltip title='Show Version History' arrow>
          <IconButton onClick={onClickVersionHistoryButton}>
            <WatchLaterIcon color='primary' />
          </IconButton>
        </Tooltip>
        {!isTemplateView && (
          <Button disabled={isLoading || !docuSignEmail} variant='secondary' onClick={() => setOpenSendFileModal(true)}>
            Send File
          </Button>
        )}
        <SplitButton
          options={saveOptions}
          disabled={
            initialDocument.noteContent === noteContent &&
            initialDocument.name === documentName &&
            initialDocument.noteTitle === noteTitle &&
            initialDocument.templateJson === JSON.stringify(templateJson)
          }
          onClick={option => handleSave(option === saveOptions[1])}
          onChange={handleChangeSaveOption}
          initialIndex={isTemplateView ? 1 : 0}
        />
      </Box>
    );
  };

  return (
    <Fragment>
      <AppBar className={styles.docAppbar} position='fixed'>
        <Toolbar className={styles.docToolbar}>
          <Box display='flex' alignItems='center' gap='1rem' height='40px'>
            <Button variant='tertiary' startIcon={<ArrowBackRoundedIcon />} onClick={handleGoBack}>
              Back
            </Button>
            <Divider orientation='vertical' variant='middle' sx={{ height: '28px' }} textAlign='center' />
            <InputBase
              className={styles.navbarText2}
              onBlur={handleSaveName}
              onChange={e => {
                setName(e.target.value);
              }}
              placeholder={`${formattedDocumentType} Name`}
              value={name}
            />
            <Tooltip title='Edit Name' arrow>
              <EditRoundedIcon sx={{ color: docGeniusPalette.primaryBlue }} />
            </Tooltip>
            <Divider orientation='vertical' variant='middle' sx={{ height: '28px' }} textAlign='center' />
            <Box className={styles.documentTag}>
              <Typography className={styles.navbarText1} style={{ color: docGeniusPalette.primaryBlue }}>
                {formattedDocumentType}
              </Typography>
            </Box>
          </Box>
          {renderButtons()}
        </Toolbar>
        <Toolbar className={styles.recipientsToolbar}>
          <RecipientSelect value={recipient} onChange={handleOnChangeRecipient} recipientOptions={recipients} />
          <Tooltip
            arrow
            title={isSidebarOpen ? 'Hide Page Thumbnails' : 'Show Page Thumbnails'}
            placement='bottom-start'
          >
            <IconButton onClick={handleToggleSidebar}>
              {isSidebarOpen ? (
                <ViewSideBar color='primary' className={styles.viewSideBarIcon} />
              ) : (
                <ViewSidebarOutlined color='primary' className={styles.viewSideBarIcon} />
              )}
            </IconButton>
          </Tooltip>
          <div id='myToolbarContainer' />
        </Toolbar>
      </AppBar>
      <EditRecipientModal
        open={openRecipientModal}
        recipients={recipients}
        onClose={handleCloseRecipientModal}
        unSelectSigner={() => {
          setRecipient(null);
        }}
      />
      <SendFileModal
        open={openSendFileModal}
        recipients={recipients}
        onClose={handleCloseSendFileModal}
        onSendFile={handleSend}
      />
    </Fragment>
  );
};

export default DocumentNavBar;
